import { Lightbulb } from '@mui/icons-material';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import LandingBoardLazy from 'components/LandingBoard/LandingBoardLazy';
import { useApi } from 'contexts/Api/Api';
import { useContent } from 'contexts/Content/Content';
import { usePage } from 'contexts/Page/Page';
import { getUrl } from 'helpers';
import { useExperiment } from 'helpers/experiments';
import { Fragment } from 'react';
import TagManager from 'react-gtm-module';
import { Container, Left, LeftBoard, LeftContent, Points, Right } from './LandingB.styled';

const LANDING_EXPERIMENT_NAME = 'plink_landing_cta_v1';
const LANDING_SURFACE = 'landing';
const APP_NAME = 'plinkromatic';

export default function LandingB() {
    const api = useApi();
    const content = useContent();
    const page = usePage();
    const { variant: experimentVariant, experiment } = useExperiment(LANDING_EXPERIMENT_NAME, LANDING_SURFACE);

    const registration = !!process.env.NEXT_PUBLIC_REGISTRATION;
    const buttonDisabled = api.loading || api.progressLoading;

    const ctaLabel = experimentVariant === 'variant_a' ? 'Start now' : content.landing.startButton;

    function fireConversion() {
        if (!experiment || !experimentVariant) {
            return;
        }
        try {
            const dataLayerName = TagManager.dataLayer.name;
            const dataLayer = (window as unknown as Record<string, unknown[]>)[dataLayerName];
            if (Array.isArray(dataLayer)) {
                dataLayer.push({
                    event: 'action_kickoff',
                    surface: LANDING_SURFACE,
                    experiment_name: experiment.name,
                    experiment_variant: experimentVariant,
                    app_name: APP_NAME,
                });
            }
        } catch {
            // Best-effort: GTM may not be initialized.
        }
    }

    function handleSubmit(e: any) {
        e.preventDefault();
        fireConversion();

        if (registration && !api.isAuthenticated) {
            api.showAuthModal('join');
            return;
        }

        page.show(api.resumeLesson || '1.1');
    }

    return (
        <Container $backgroundImage={content.landing.background}>
            <Left>
                <LeftContent>
                    <Typography
                        component="h1"
                        data-testid="landing-heading"
                        variant="montserrat848"
                        sx={{ marginBottom: registration ? 0 : 5 }}
                    >
                        {content.landing.title}
                    </Typography>
                    <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: registration ? 340 : undefined }}>
                        <Box sx={{ display: 'inline-block' }}>
                            <Button
                                aria-label={ctaLabel}
                                data-testid="landing-start-button"
                                data-experiment-variant={experimentVariant ?? 'control'}
                                disabled={buttonDisabled}
                                size="large"
                                startIcon={
                                    api.loading ? <CircularProgress color="inherit" size="18px" /> : <Lightbulb />
                                }
                                sx={{ textTransform: 'uppercase' }}
                                type="submit"
                            >
                                {ctaLabel}
                            </Button>
                        </Box>
                    </Box>
                </LeftContent>
                <Box sx={{ position: 'relative' }}>
                    <LeftBoard>
                        <LandingBoardLazy />
                    </LeftBoard>
                </Box>
            </Left>
            <Right>
                <Typography
                    data-testid="landing-text"
                    dangerouslySetInnerHTML={{ __html: content.parseHtml(content.landing.text) }}
                    variant="montserrat418"
                    sx={{ display: 'flex', flexDirection: 'column', gap: 3, marginBottom: 5 }}
                />
                <Points>
                    {[
                        content.landing.point1,
                        content.landing.point2,
                        content.landing.point3,
                        content.landing.point4,
                        content.landing.point5,
                        content.landing.point6,
                    ].map((point, index) => (
                        <Fragment key={index}>
                            <picture>
                                <img alt={point.iconAlt} src={getUrl(point.icon)} />
                            </picture>
                            <Typography variant="montserrat418">{point.label}</Typography>
                        </Fragment>
                    ))}
                </Points>
            </Right>
        </Container>
    );
}
